import React, { useMemo, useRef } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import I3PageHero from '../components/I3PageHero';
import { useRevealSections } from '../hooks/useReveal';
import {
  estimateReadMinutes,
  getI3BlogPostBySlug,
  isI3BlogQuoteOnly,
} from '../data/i3BlogPosts';
import { useI3SiteContent } from '../cms/useI3SiteContent';
import {
  cmsPostToLegacy,
  findCmsBlogPost,
  isBlogPostArchived,
  isBlogPostPubliclyVisible,
} from '../cms/blogVisibility';
import '../styles/I3Page.css';
import '../styles/I3Reportage.css';
import '../styles/I3Blog.css';

function formatPostDate(iso: string): string {
  try {
    const d = new Date(`${iso}T12:00:00`);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

const I3BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { content } = useI3SiteContent();
  const wrapRef = useRef<HTMLDivElement>(null);
  useRevealSections(wrapRef);

  const { post, archived } = useMemo(() => {
    const cms = findCmsBlogPost(content.blogPosts, slug);
    if (cms) {
      if (isBlogPostArchived(cms)) {
        return { post: cmsPostToLegacy(cms), archived: true as const };
      }
      if (!isBlogPostPubliclyVisible(cms)) {
        return { post: undefined, archived: false as const };
      }
      return { post: cmsPostToLegacy(cms), archived: false as const };
    }
    return { post: getI3BlogPostBySlug(slug), archived: false as const };
  }, [content.blogPosts, slug]);

  if (post && isI3BlogQuoteOnly(post)) {
    return <Navigate to="/blog" replace />;
  }

  if (!post) {
    return (
      <article className="i3-page i3-reportage-page i3-blog-page">
        <I3PageHero showLogo title="Not found" subtitle="This article may have moved or is not published yet." />
        <div className="i3-page-wrap i3-blog-not-found">
          <Link to="/blog" className="i3-cta i3-cta-primary">
            Back to journal
          </Link>
        </div>
      </article>
    );
  }

  const topicLabel = post.topic?.trim();
  const hasEditorial = !!(post.quote?.trim() || post.meaning?.trim());
  const subtitle = [
    topicLabel,
    formatPostDate(post.publishedAt),
    post.author,
    `${estimateReadMinutes(post)} min read`,
  ]
    .filter(Boolean)
    .join(' · ');

  const editorialBody = (
    <>
      {archived ? (
        <p className="i3-blog-archived-notice i3-reveal" role="status">
          This article has been archived. It is no longer shown on the journal index.
        </p>
      ) : null}
      <p className="i3-blog-editorial-back i3-reveal">
        <Link to="/blog" className="i3-blog-back-link">
          ← Journal
        </Link>
      </p>
      {post.heroImage ? (
        <figure className="i3-blog-editorial-hero-figure i3-reveal">
          <img src={post.heroImage} alt="" className="i3-blog-editorial-hero-img" />
        </figure>
      ) : null}
      <div className="i3-blog-editorial-stack">
        <header className="i3-blog-editorial-head i3-reveal">
        <h1 className="i3-blog-editorial-title">{post.title}</h1>
        <p className="i3-blog-editorial-date">{formatPostDate(post.publishedAt)}</p>
        {(topicLabel || post.author) && (
          <p className="i3-blog-editorial-byline">
            {[topicLabel, post.author].filter(Boolean).join(' · ')}
          </p>
        )}
      </header>
      {post.quote?.trim() ? (
        <blockquote className="i3-blog-editorial-quote i3-reveal">
          <p className="i3-blog-editorial-quote-p">“{post.quote.trim()}”</p>
        </blockquote>
      ) : null}
      {post.meaning?.trim() ? (
        <p className="i3-blog-editorial-meaning i3-reveal">
          <span className="i3-blog-editorial-meaning-lead">Meaning:</span>{' '}
          <span className="i3-blog-editorial-meaning-text">{post.meaning.trim()}</span>
        </p>
      ) : null}
      {post.paragraphs.length > 0 ? (
        <div className="i3-blog-article i3-blog-article--editorial-follow i3-reveal">
          {post.paragraphs.map((p, i) => (
            <p key={i} className="i3-blog-article-p">
              {p}
            </p>
          ))}
        </div>
      ) : null}
      </div>
      <p className="i3-blog-back i3-blog-back--editorial i3-reveal">
        <Link to="/blog" className="i3-blog-back-link">
          ← All articles
        </Link>
      </p>
    </>
  );

  if (hasEditorial) {
    return (
      <article className="i3-page i3-reportage-page i3-blog-page i3-blog-page--editorial">
        <div className="i3-page-wrap i3-blog-editorial-page" ref={wrapRef}>
          {editorialBody}
        </div>
      </article>
    );
  }

  return (
    <article className="i3-page i3-reportage-page i3-blog-page">
      <I3PageHero showLogo title={post.title} subtitle={subtitle} />
      <div className="i3-page-wrap" ref={wrapRef}>
        {archived ? (
          <p className="i3-blog-archived-notice i3-reveal" role="status">
            This article has been archived. It is no longer shown on the journal index.
          </p>
        ) : null}
        {post.heroImage ? (
          <figure className="i3-blog-hero-figure i3-reveal">
            <img src={post.heroImage} alt="" className="i3-blog-hero-img" />
          </figure>
        ) : null}
        <div className="i3-blog-article i3-reveal">
          {post.paragraphs.map((p, i) => (
            <p key={i} className="i3-blog-article-p">
              {p}
            </p>
          ))}
        </div>
        <p className="i3-blog-back i3-reveal">
          <Link to="/blog" className="i3-blog-back-link">
            ← All articles
          </Link>
        </p>
      </div>
    </article>
  );
};

export default I3BlogPost;
